import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import type { SubscriptionKind } from "./subscribeTypes";
import { useSubscriptionStatus, useToggleSubscription } from "./useSubscription";

export interface SubscribeButtonProps {
  kind: SubscriptionKind;
  targetId: string | null | undefined;
  /** `icon` is the bell alone (a document header); `full` carries the word too. */
  variant?: "icon" | "full";
  className?: string;
}

/**
 * The bell. One primitive, every host, every kind of thing.
 *
 * Three rules it will not bend:
 *  1. It renders NOTHING until the server has answered. A bell that guesses "not subscribed"
 *     and then flips is worse than a beat of absence.
 *  2. It renders nothing for an UNWIRED kind — the server says which kinds have a producer,
 *     and offering to subscribe where nothing will ever publish is a promise the platform
 *     cannot keep.
 *  3. It never claims success it did not get: the state comes from the refetched row, not
 *     from optimism.
 */
export function SubscribeButton({
  kind,
  targetId,
  variant = "icon",
  className,
}: SubscribeButtonProps) {
  const { t } = useTranslation();
  const status = useSubscriptionStatus(kind, targetId);
  const toggle = useToggleSubscription(kind, targetId);

  if (!targetId || status.isPending || status.isError || !status.data) {
    return null;
  }

  if (!status.data.wired) {
    return null;
  }

  const subscribed = status.data.subscribed;
  const label = subscribed ? t("Unsubscribe") : t("Subscribe");
  const title = subscribed
    ? t("You are notified when this changes")
    : t("Get notified when this changes");

  return (
    <button
      type="button"
      title={title}
      aria-label={label}
      aria-pressed={subscribed}
      disabled={toggle.isPending}
      onClick={() =>
        toggle.mutate(!subscribed, {
          onSuccess: (data) =>
            toast.success(data.subscribed ? t("Subscribed") : t("Unsubscribed")),
          onError: () => toast.error(t("Could not change your subscription")),
        })
      }
      className={
        className ??
        [
          "inline-flex cursor-pointer items-center gap-1.5 rounded-md border px-2.5 py-1 text-sm",
          "border-[color:var(--divider)] bg-transparent",
          subscribed
            ? "text-[color:var(--accent)]"
            : "text-[color:var(--text-secondary)] hover:text-[color:var(--accent)]",
          toggle.isPending ? "opacity-60" : "",
        ].join(" ")
      }
    >
      {/* An inline glyph rather than an icon dependency: this button mounts in hosts whose
          icon packs differ, and the bell must look the same in all of them. */}
      <span aria-hidden="true">{subscribed ? "🔔" : "🔕"}</span>
      {variant === "full" ? <span>{label}</span> : null}
    </button>
  );
}
